import { LinearProgress } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { useQuery } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import orderBy from 'lodash/orderBy';
import React from 'react';
import { PaperExpandable } from '../../components/paper-expandable';
import { RouteContainer } from '../../components/route-container';
import { searchBy, SearchField } from '../../components/search-field';
import { TableColumnHeaders } from '../../components/table-column-headers';
import { AuthContext } from '../../contexts/auth-context';
import { ColumnHeader, MyStaffData } from '../../types';
import { captureError } from '../../utils/capture-error';
import { isAgt } from '../../utils/is-agent';
import { subarray } from '../../utils/subarray';
import { StaffAdd } from './staff-add';
import { StaffRow } from './staff-row';

export const tableColumnsStaff: ColumnHeader<MyStaffData>[] = [
  { key: 'Username' },
  { key: 'Active', label: 'Status' },
  { key: 'LName', label: 'Name' },
  { key: 'Email' },
];

export function MyStaff() {
  // Context
  const {
    state: { user, impersonatedAgent },
  } = React.useContext(AuthContext);

  // State
  const [searchString, setSearchString] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [sort, setSort] = React.useState<'asc' | 'desc'>('asc');
  const [sortBy, setSortBy] =
    React.useState<ColumnHeader<MyStaffData>['key']>('Username');

  const enabled = impersonatedAgent === null;
  const AgtNo = user?.getUsername();
  let impersonateWarning: JSX.Element | null = null;
  if (!enabled) {
    impersonateWarning = (
      <Box sx={{ width: '100%' }}>
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          Impersonation does not work on this page.
        </Alert>
      </Box>
    );
  }

  // Query - Get My Staff
  const path = '/my-staff';
  const queryKey = [path, { AgtNo }];
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (isAgt(AgtNo)) {
        const response: {
          data: MyStaffData[];
        } = await API.post('ContractingAPI', path, {});

        return response.data || [];
      }

      return [];
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Used by TableHead
  const handleSort =
    (property: ColumnHeader<MyStaffData>['key']) =>
    (event: React.MouseEvent<unknown>) => {
      if (property === sortBy) {
        // User is changing sort of current column
        setSort((currentState) => (currentState === 'asc' ? 'desc' : 'asc'));
      } else {
        // User is changing sort of new column
        setSortBy(property);
        setSort('asc');
      }
    };

  // Used by TablePagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Used by TablePagination
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Searching
  const searchedItems = searchBy(query.data || [], searchString);
  // Sorting
  const sortedItems = orderBy(searchedItems, sortBy, sort);
  // Paging
  const start = page * rowsPerPage;
  const end = start + rowsPerPage;
  const renderItems = subarray({ data: sortedItems || [], start, end });

  const isLoading = query.isLoading || query.isFetching;

  return (
    <RouteContainer routeTitle="My Staff" withContainer>
      <Box sx={{ pt: 2, pb: 16 }}>
        {impersonateWarning}
        <PaperExpandable
          title="Staff Accounts"
          count={renderItems.length}
          description="Create and manage staff accounts that impersonate as your agent account"
          open={true}
        >
          <React.Fragment>
            <Stack
              spacing={{ xs: 1, sm: 2 }}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems="center"
              sx={{ px: 2, pt: { xs: 2, sm: 0 } }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <SearchField
                  id="admin-search-contracts"
                  placeholder="Search contracts"
                  value={searchString}
                  onChange={(event) => {
                    setSearchString(event.target.value);
                    // Reset page
                    setPage(0);
                  }}
                />

                {isAgt(AgtNo) ? <StaffAdd queryKey={queryKey} /> : null}
              </Box>
              <Box flex={1} sx={{ display: { xs: 'none', md: 'flex' } }} />

              <TablePagination
                width="100%"
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={sortedItems.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Stack>

            {isLoading ? <LinearProgress color="secondary" /> : <Divider />}

            {renderItems.length > 0 ? (
              <TableContainer sx={{ height: '65dvh' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableColumnHeaders
                        sort={sort}
                        sortBy={sortBy}
                        tableColumns={tableColumnsStaff}
                        sortHandler={handleSort}
                        hideSortIcon
                      />
                      <TableCell align="center">Commissions</TableCell>
                      <TableCell align="center">Debt</TableCell>
                      <TableCell align="center">Profile</TableCell>
                      <TableCell align="center">Wallet</TableCell>
                      <TableCell align="center">Manage</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {renderItems.map((row) => {
                      return (
                        <StaffRow
                          key={row.AgtStaffId}
                          row={row}
                          queryKey={queryKey}
                        />
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box
                sx={{
                  height: '35dvh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Roboto Mono',
                  color: '#9b9b9b',
                }}
              >
                {isLoading ? 'Searching for staff...' : 'No staff found'}
              </Box>
            )}
          </React.Fragment>
        </PaperExpandable>
      </Box>
    </RouteContainer>
  );
}
